import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { QRCodeSVG } from 'qrcode.react';
import NavBar from '../components/navbar/navbar';
import './mfaSettings.scss';
import { useTranslation } from 'react-i18next';
import { View, Button, Heading } from "@aws-amplify/ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

const MFASettings = ({ user, signOut }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [setupUri, setSetupUri] = useState(null);
  const [sharedSecret, setSharedSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const userData = await new Promise((resolve, reject) => {
        cognitoUser.getUserData((err, data) => {
          if (err) reject(err);
          else resolve(data || {});
        }, { bypassCache: true });
      });
      const preferredMFA = userData.PreferredMfaSetting;
      const mfaList = userData.UserMFASettingList || [];
      setMfaEnabled(
        preferredMFA === 'SOFTWARE_TOKEN_MFA' ||
        preferredMFA === 'TOTP' ||
        mfaList.includes('SOFTWARE_TOKEN_MFA') ||
        mfaList.includes('TOTP')
      );
    } catch (err) {
      console.error('Error checking MFA status:', err);
    } finally {
      setMfaStatusLoading(false);
    }
  };

  const startMFASetup = async () => {
    try {
      setLoading(true);
      setError('');
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const code = await Auth.setupTOTP(cognitoUser);
      
      // Generate setup URI for QR code
      const appName = 'LoginHub';
      const uri = `otpauth://totp/${appName}:${cognitoUser.username}?secret=${code}&issuer=${appName}`;
      
      setSetupUri(uri);
      setSharedSecret(code);
      setIsSettingUp(true);
    } catch (err) {
      setError('Failed to start MFA setup: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnableMFA = async () => {
    try {
      setLoading(true);
      setError('');
      
      const cognitoUser = await Auth.currentAuthenticatedUser();
      
      await Auth.verifyTotpToken(cognitoUser, verificationCode);
      
      await Auth.setPreferredMFA(cognitoUser, 'TOTP');
      
      setMfaEnabled(true);
      setIsSettingUp(false);
      setSetupUri(null);
      setSharedSecret(null);
      setVerificationCode('');
    } catch (err) {
      setError(t("MFASETTINGS.invalidVerificationCode"));
      console.error('MFA verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!window.confirm(t("MFASETTINGS.disableConfirmation"))) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await new Promise((resolve, reject) => {
        cognitoUser.setUserMfaPreference(
          null,
          { PreferredMfa: false, Enabled: false },
          (err, result) => {
            if (err) reject(err);
            else resolve(result);
          }
        );
      });
    setMfaEnabled(false);
    } catch (err) {
      setError('Failed to disable MFA: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelSetup = () => {
    setIsSettingUp(false);
    setSetupUri(null);
    setSharedSecret(null);
    setVerificationCode('');
    setError('');
  };

  const handleGoBack = () => {
    navigate("/home");
  };

  const handleCopySecret = () => {
    if (sharedSecret) {
      navigator.clipboard.writeText(sharedSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

    return mfaStatusLoading ? (
      <div className="mfa-spinner">
        <div className="mfa-spinner-circle"></div>
      </div>
    ) : (
    <View className='page-container'>
      <NavBar user={user} signOut={signOut} />
      <Button className='back-button' onClick={handleGoBack}>
        &lt; {t("SUBSCRIPTIONMANAGEMENT.backToHome")}
      </Button>
      <Heading
        className='text-color-mfa-page'
        level={3}
        textAlign={"center"}
      >
        {t("MFASETTINGS.title")}
      </Heading>
        
      <View className='mfa-content-container'>
        {error && <div className="error-message">{error}</div>}
        
        {!isSettingUp && !mfaEnabled && (
          <View className='mfa-section'>
            <p className='section-description'>
              {t("MFASETTINGS.description")}
            </p>
            <p className='section-subdescription'>
              {t("MFASETTINGS.appRequired")}
            </p>
            <View className='bottom-button-container'>
              <Button 
                onClick={startMFASetup} 
                disabled={loading}
                variation='default'
                className='save-button'
              >
                {loading ? t("MFASETTINGS.loading") : t("MFASETTINGS.enableMFA")}
              </Button>
            </View>
          </View>
        )}

        {!isSettingUp && mfaEnabled && (
          <View className='mfa-section'>
            <div className="status-badge">{t("MFASETTINGS.enabled")}</div>
            <p className='section-description'>
              {t("MFASETTINGS.enabledDescription")}
            </p>
            <View className='bottom-button-container'>
              <Button 
                onClick={disableMFA} 
                disabled={loading}
                variation='default'
                className='danger-button'
              >
                {loading ? t("MFASETTINGS.loading") : t("MFASETTINGS.disableMFA")}
              </Button>
            </View>
          </View>
        )}

        {isSettingUp && (
          <View className='mfa-section'>
            <Heading level={5} className='setup-heading'>
              {t("MFASETTINGS.setupTitle")}
            </Heading>
            
            <div className="setup-instructions">
              <ol>
                <li>
                  <strong>{t("MFASETTINGS.step1")}</strong>
                  <ul>
                    <li>Google Authenticator</li>
                    <li>Microsoft Authenticator</li>
                  </ul>
                </li>
                
                <li>
                  <strong>{t("MFASETTINGS.step2")}</strong>
                  <div className="qr-code-container">
                    {setupUri && <QRCodeSVG value={setupUri} size={200} />}
                  </div>
                </li>
                
                <li>
                  <strong>{t("MFASETTINGS.step3")}</strong>
                  <div className="secret-key" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <code>{sharedSecret}</code>
                    <button
                      onClick={handleCopySecret}
                      disabled={!sharedSecret}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: sharedSecret ? "pointer" : "not-allowed",
                        padding: 0,
                        display: "flex",
                        alignItems: "center"
                      }}
                      aria-label={t("MFASETTINGS.copy")}
                    >
                      <FontAwesomeIcon icon={faCopy} color={copied ? "#012e91" : "#333"} />
                    </button>
                    {copied && (
                      <span style={{ color: "#012e91", fontSize: "0.95em" }}>
                        {t("MFASETTINGS.copied")}
                      </span>
                    )}
                  </div>
                </li>
                
                <li>
                  <strong>{t("MFASETTINGS.step4")}</strong>
                  <div className="verification-input">
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength="6"
                      className="code-input"
                    />
                  </div>
                </li>
              </ol>
            </div>
            
            <View className='bottom-button-container'>
              <Button 
                onClick={verifyAndEnableMFA} 
                disabled={loading || verificationCode.length !== 6}
                variation='default'
                className='save-button'
              >
                {loading ? t("MFASETTINGS.verifying") : t("MFASETTINGS.verifyAndEnable")}
              </Button>
              <Button 
                onClick={cancelSetup} 
                disabled={loading}
                variation='link'
                className='reset-button'
              >
                {t("MFASETTINGS.cancel")}
              </Button>
            </View>
          </View>
        )}
      </View>
    </View>
  )
};

export default MFASettings;